import type { ReactNode } from 'react';
import { cn } from './cn';
import { PALETTE } from '../lib/palette';
import { fmtQty } from '../lib/format';

export type StatTone = 'brand' | 'gold' | 'sky' | 'success' | 'warning' | 'danger' | 'neutral';

/**
 * Per-tone styling. `tint` is the RESTING treatment — the gradient stop + hairline that give every
 * tile its metric's own colour; `wash` is the SELECTED (filter-active) one, the same hue at roughly
 * twice the strength and as a solid fill rather than a fade.
 *
 * Selection deliberately reuses the card's OWN tone instead of a single brand-green highlight. A
 * green ring on a gold or red KPI put two unrelated hues on one card, so the eye read "green thing"
 * before "selected thing" — colour has to keep meaning the metric's category, and selection has to be
 * legible on all seven tones. Depth does the rest: the left bar thickens 4px -> 6px, so the state is
 * still readable without relying on colour alone.
 */
const ACCENT: Record<StatTone, { border: string; chip: string; tint: string; wash: string }> = {
  brand: {
    border: 'border-l-brand-500',
    chip: 'bg-brand-100 text-brand-700 dark:bg-brand-400/20 dark:text-brand-300',
    tint: 'border-brand-500/25 from-brand-500/[0.10] dark:from-brand-500/[0.14]',
    wash: 'bg-brand-500/10 border-brand-500/45 from-brand-500/[0.14] dark:from-brand-500/[0.18]',
  },
  gold: {
    border: 'border-l-harvest-400',
    chip: 'bg-harvest-100 text-harvest-700 dark:bg-harvest-400/20 dark:text-harvest-300',
    tint: 'border-harvest-400/35 from-harvest-400/[0.14] dark:from-harvest-400/[0.16]',
    wash: 'bg-harvest-400/14 border-harvest-400/55 from-harvest-400/[0.20] dark:from-harvest-400/[0.22]',
  },
  sky: {
    border: 'border-l-sky-500',
    chip: 'bg-sky-400/15 text-sky-600 dark:bg-sky-400/20 dark:text-sky-300',
    tint: 'border-sky-500/25 from-sky-500/[0.10] dark:from-sky-500/[0.14]',
    wash: 'bg-sky-500/10 border-sky-500/45 from-sky-500/[0.14] dark:from-sky-500/[0.18]',
  },
  success: {
    border: 'border-l-brand-500',
    chip: 'bg-brand-100 text-brand-700 dark:bg-brand-400/20 dark:text-brand-300',
    tint: 'border-brand-500/25 from-brand-500/[0.10] dark:from-brand-500/[0.14]',
    wash: 'bg-brand-500/10 border-brand-500/45 from-brand-500/[0.14] dark:from-brand-500/[0.18]',
  },
  warning: {
    border: 'border-l-warning-500',
    chip: 'bg-warning-500/15 text-warning-600 dark:bg-warning-500/20 dark:text-harvest-300',
    tint: 'border-warning-500/30 from-warning-500/[0.13] dark:from-warning-500/[0.16]',
    wash: 'bg-warning-500/14 border-warning-500/50 from-warning-500/[0.18] dark:from-warning-500/[0.22]',
  },
  danger: {
    border: 'border-l-danger-500',
    chip: 'bg-danger-500/12 text-danger-600 dark:bg-danger-500/20 dark:text-danger-400',
    tint: 'border-danger-500/25 from-danger-500/[0.10] dark:from-danger-500/[0.13]',
    wash: 'bg-danger-500/10 border-danger-500/45 from-danger-500/[0.15] dark:from-danger-500/[0.18]',
  },
  neutral: {
    border: 'border-l-line-strong',
    chip: 'bg-surface-3 text-muted',
    tint: 'border-line from-line/40 dark:from-line/50',
    wash: 'bg-surface-3 border-line-strong from-line/60 dark:from-line/60',
  },
};

/**
 * Every palette hex, uppercased, mapped to the tone it means.
 *
 * WHY A MAP AND NOT A CHAIN OF `===`: the chain this replaces uppercased only the INPUT and then
 * compared it against `PALETTE`'s lowercase literals, so `'#B51D29' === '#b51d29'` was false and
 * EVERY hex-accented KPI in the app silently fell through to grey. It went unnoticed because the
 * one hex that did work — brand `#339022` — is all digits, so uppercasing it is a no-op. Normalising
 * both sides in one place makes that class of bug impossible to reintroduce, and covering every
 * shade means a page that reaches for `brand[400]` gets green instead of grey.
 *
 * Insertion order is precedence: harvest is claimed before warning because they share `#e7b22c`,
 * and the first family to claim a hex keeps it (matching the old chain's order).
 */
const HEX_TONE: ReadonlyMap<string, StatTone> = (() => {
  const m = new Map<string, StatTone>();
  const claim = (shades: readonly string[], tone: StatTone) => {
    for (const hex of shades) {
      const key = hex.toUpperCase();
      if (!m.has(key)) m.set(key, tone);
    }
  };
  claim(Object.values(PALETTE.sky), 'sky');
  claim(Object.values(PALETTE.danger), 'danger');
  claim(Object.values(PALETTE.harvest), 'gold');
  claim(Object.values(PALETTE.warning), 'warning');
  claim(Object.values(PALETTE.success), 'success');
  claim(Object.values(PALETTE.brand), 'brand');
  return m;
})();

/**
 * Legacy accent -> tone. Before StatCard was adopted, every page tinted its own KPI tile with either
 * an antd-era hex (`#D9363E`) or a Tailwind utility (`border-l-danger-500`), and both spellings are
 * still live at call sites. Resolving them in ONE place means the pages finally agree on what a
 * colour means — danger vs warning vs informational — instead of each picking its own near-miss red.
 * Prefer passing `tone` directly in new code; this exists so the remaining pages can delegate to
 * StatCard without rewriting every call site in the same change.
 *
 * Tailwind spellings are matched first: a class name carries the family in its text, and no hex can
 * contain those letters, so the two spellings can never collide.
 */
export function statTone(accent?: string): StatTone {
  const a = (accent ?? '').trim().toUpperCase();
  if (!a) return 'neutral';
  if (a.includes('SKY')) return 'sky';
  if (a.includes('DANGER')) return 'danger';
  if (a.includes('HARVEST')) return 'gold';
  if (a.includes('WARNING')) return 'warning';
  if (a.includes('SUCCESS')) return 'success';
  if (a.includes('BRAND')) return 'brand';
  return HEX_TONE.get(a) ?? 'neutral';
}

/**
 * A KPI VALUE THAT CARRIES ITS OWN UNIT.
 *
 * "what is 30, 30 units" — asked of a tile reading a bare `30`. A KPI is read on its own, a glance
 * at a time, so the unit cannot live two cards away on the one tile that happened to say it; and a
 * band whose tiles count different SUBJECTS (62 adjustments beside 836 units) reads as numbers that
 * refuse to reconcile until each says which it is.
 *
 * The unit is set in a quiet lighter weight beside the figure, so the figure is still the loudest
 * thing on the tile. Three pages had grown their own copy of this — it lives here now so the next
 * band gets it for free.
 *
 *   <StatCard label="Waiting for approval" value={<Counted n={4} one="return" many="returns" />} />
 */
export const Counted = ({ n, one, many }: { n: any; one: string; many: string }) => (
  <>
    {fmtQty(n)} <span className="type-label font-normal text-muted">{Number(n ?? 0) === 1 ? one : many}</span>
  </>
);

export interface StatCardProps {
  label: ReactNode;
  /** The value. Pass a `<CountUp>` from `ui/motion` here when you want it to animate. */
  value: ReactNode;
  /** Optional secondary line under the value (e.g. "vs last month", a sublabel). */
  hint?: ReactNode;
  /** A delta chip, e.g. "+12%". `direction` colours it. */
  delta?: { label: ReactNode; direction?: 'up' | 'down' | 'flat' };
  icon?: ReactNode;
  /** Accent colour for the left border + icon chip. */
  tone?: StatTone;
  /** Makes the whole card a keyboard-accessible button (for click-to-filter/drill). */
  onClick?: () => void;
  /** Filter-active/selected. Tints the card in its OWN tone and thickens the left bar — pass this
   *  rather than hand-rolling a highlight class, so selection looks the same on every page. */
  active?: boolean;
  className?: string;
}

/**
 * One KPI card for the whole app — the audit found 5+ different KPI treatments
 * (inline-hex `Kpi`, antd `Statistic`, bespoke `kpi()`/`KpiTile`) with broken dark
 * mode and low-contrast value text. This renders values in `text-content` (legible),
 * keeps the accent to the border/icon only, flips correctly in dark mode, and is
 * keyboard-accessible when clickable. Count-up lives in the value slot (`ui/motion`),
 * so this file stays free of the GSAP dependency.
 */
export function StatCard({ label, value, hint, delta, icon, tone = 'brand', onClick, active, className }: StatCardProps) {
  const a = ACCENT[tone];
  const clickable = !!onClick;
  const deltaCls =
    delta?.direction === 'up'
      ? 'bg-brand-500/12 text-brand-700 dark:text-brand-300'
      : delta?.direction === 'down'
        ? 'bg-danger-500/10 text-danger-600 dark:text-danger-500'
        : 'bg-surface-3 text-muted';

  const Comp: 'button' | 'div' = clickable ? 'button' : 'div';
  return (
    <Comp
      {...(clickable ? { type: 'button' as const, onClick, 'aria-pressed': active ?? undefined } : {})}
      className={cn(
        // `w-full` is load-bearing: when `onClick` is set this root becomes a <button>, and a button
        // is a form control whose `width: auto` resolves to FIT-CONTENT, not stretch. Without it a
        // clickable KPI shrank to its own text while its non-clickable siblings (plain <div>) filled
        // the column — so a row of filter KPIs rendered at four different widths.
        // `h-full` so a tile fills a stretched row/grid cell: tiles differ by a line (one carries a
        // hint, another doesn't), and without it each card sizes to its own text and the row's bottom
        // edge goes ragged. Harmless when the parent's height is auto.
        // `@container`: the value's size has to answer to the CARD's width, not the viewport's. The
        // same tile is a sixth of a desktop row and the full width of a phone — a viewport breakpoint
        // gets one of those two wrong every time.
        '@container flex h-full w-full items-start gap-3 rounded-card border p-4 text-left',
        // TINT: the tile carries its own metric's hue as a top-down gradient (the tone supplies the
        // `from-*` stop; this sets the direction and the fade-out). A row of six white rectangles
        // has to be read label-by-label; the same row tinted is navigable at a glance, which is the
        // whole job of a KPI band. It stays a WASH that dies out by 75% of the height rather than a
        // fill, so the figure — not the box — is still the loudest thing on the tile.
        'bg-linear-to-b to-75% to-transparent',
        // Selected = a stronger, SOLID wash of the same tone, a 6px rail instead of 4px, and a step
        // up the elevation ladder. Now that every tile is tinted, "selected" can no longer be
        // signalled by tint alone, so it is carried by depth + weight as well.
        // `bg-surface`/`shadow-*` are applied inside the branches and never in the base: two
        // background-color (or two box-shadow) utilities on one element resolve by STYLESHEET order
        // (Tailwind's), not by class order — that is how the old wash silently lost to `bg-surface`
        // and left the card white. Mutually exclusive branches remove the conflict entirely.
        active ? cn('border-l-[6px] shadow-e2', a.wash) : cn('border-l-4 bg-surface shadow-e1', a.tint),
        a.border,
        clickable &&
          // Hover RAISES instead of moving. `-translate-y` on a grid of six tiles made the whole
          // band twitch as the pointer crossed it, and a tile that moves reads as draggable rather
          // than clickable. A `hover:` rule carries the pseudo-class's extra specificity, so it
          // beats the resting `shadow-e1` no matter what order Tailwind emits them in.
          'cursor-pointer transition-shadow duration-150 hover:shadow-e2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
        className,
      )}
    >
      {icon && (
        // Under an 11rem tile the icon is dropped: it costs 52px of the row, and on a tile that small
        // the FIGURE is the only thing that has to survive — the left accent bar still carries the tone.
        // (Without this a two-up phone KPI truncated "₹22,88,758" to fit an icon beside it.)
        <span className={cn('@max-[11rem]:hidden grid size-10 shrink-0 place-items-center rounded-control [&>svg]:size-5', a.chip)}>
          {icon}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate type-label text-muted">{label}</div>
        <div className="mt-1 flex min-w-0 items-baseline gap-2">
          {/* A money KPI is the widest thing on the card and the one that must never be cut. Below a
              ~15rem card the metric steps down to its small size (₹10,49,740.80 at 24px needs 166px,
              which a half-width phone tile does not have), and `truncate` is the last-resort guard so
              a pathological value clips INSIDE the card instead of printing across the one beside it. */}
          <span className="type-metric @max-[15rem]:type-metric-sm min-w-0 truncate text-content"
            title={typeof value === 'string' || typeof value === 'number' ? String(value) : undefined}>
            {value}
          </span>
          {/* The chip's tinted background already carries the emphasis, so the text stays at the
              caption weight — one emphasis signal, not two (see the type-scale rules in theme.css). */}
          {delta && (
            <span className={cn('shrink-0 rounded-pill px-1.5 py-px type-caption', deltaCls)}>
              {delta.label}
            </span>
          )}
        </div>
        {/* A hint is the value's explanation — most often the breakdown that has to ADD UP to the
            metric above it — so truncating it to one line destroyed the only thing it was there to
            say ("277 active · 36 onboarding · 1 suspended/clo…"). Two lines, then clamp: the tile
            can breathe without one long hint stretching a whole row of KPIs. `title` keeps the full
            text reachable on hover whenever the caller passed a plain string. */}
        {hint && (
          <div className="mt-1 line-clamp-2 type-label text-subtle" title={typeof hint === 'string' ? hint : undefined}>
            {hint}
          </div>
        )}
      </div>
    </Comp>
  );
}
